import { atom } from "jotai";

// 아이디·비밀번호 찾기 step (SignUp 본인인증 흐름과 동일 룩앤필)
//   1: 생년월일 + 성별 (KR 만 — 다날 본인인증 필수)
//   2: 이름+통신사+전화번호 (KR) / 국가코드+전화번호 (해외)
//   3: OTP 인증 (다날 / SMS) + find-account 호출
//   4: 결과 (이메일 마스킹 + provider 분기 또는 가입 이력 없음)
//   5: 새 비밀번호 입력 (provider=EMAIL 일 때만)
//   6: 완료
//
// 진입 시:
//   - KR  → stack [1]
//   - 해외 → stack [2] (생년월일/성별 불필요)
export const recoverStepStackAtom = atom<number[]>([1]);

export const recoverStepAtom = atom((get) => {
    const stack = get(recoverStepStackAtom);
    return stack[stack.length - 1] ?? 1;
});

// recovery 페이지의 /account-recovery URL 에 쌓인 history pushState entry 개수.
//   초기 nav 는 포함하지 않음 (별도로 +1 해서 사용).
//   pushStep → +1, popstate(device back) → -1.
//   collapseToInitialAndPushRecoverStep / replaceLastRecoverStep 은 replaceState 라 변동 없음.
//   사용 케이스: Step4(미가입) → /sign-up → device back → /ar 재진입 시 모든 /ar entry pop 하고 /signin 으로 직행.
export const recoverHistoryDepthAtom = atom(0);

export const decrementRecoverHistoryDepthAtom = atom(
    null,
    (get, set) => set(recoverHistoryDepthAtom, Math.max(0, get(recoverHistoryDepthAtom) - 1))
);

// Recovery 흐름에서 /signin 으로 빠져나가는 동안 popstate cascade 발생 — 그동안 popstate handler 가
// popStep / decrementDepth / navigate(-1) 등을 호출하면 race 가 일어나므로 suppress flag.
//   exit 시작 시 setRecoveryExiting(true), 다음 /account-recovery 마운트 시 false 로 reset.
let _isExitingRecovery = false;
export const isExitingRecovery = () => _isExitingRecovery;
export const setRecoveryExiting = (v: boolean) => { _isExitingRecovery = v; };

export const pushRecoverStepAtom = atom(
    null,
    (get, set, nextStep: number) => {
        const stack = get(recoverStepStackAtom);
        set(recoverStepStackAtom, [...stack, nextStep]);
        set(recoverHistoryDepthAtom, get(recoverHistoryDepthAtom) + 1);
        history.pushState({ recoverStep: nextStep }, "");
    }
);

export const popRecoverStepAtom = atom(
    null,
    (get, set): number | null => {
        const stack = get(recoverStepStackAtom);
        if (stack.length <= 1) return null;
        const newStack = stack.slice(0, -1);
        set(recoverStepStackAtom, newStack);
        return newStack[newStack.length - 1];
    }
);

// 결과 화면(Step3) 분기 — 가입자/SNS/미가입을 step replace 로 깔끔히 분기하고 싶을 때
export const replaceRecoverStepAtom = atom(
    null,
    (_get, set, nextStep: number) => {
        set(recoverStepStackAtom, [nextStep]);
        history.replaceState({ recoverStep: nextStep }, "");
    }
);

// 마지막 step 만 교체 — 이전 step 들은 그대로 둠.
//   사용 케이스: 일회성 화면(OTP 등) 자리에 다음 step 을 replace 로 끼워넣고 싶을 때.
export const replaceLastRecoverStepAtom = atom(
    null,
    (get, set, nextStep: number) => {
        const stack = get(recoverStepStackAtom);
        const newStack = [...stack.slice(0, -1), nextStep];
        set(recoverStepStackAtom, newStack);
        history.replaceState({ recoverStep: nextStep }, "");
    }
);

// 본인인증 시작점(stack[0])만 남기고 nextStep 추가.
//   사용 케이스: OTP 성공 → 결과 화면 진입 시. device back 시 OTP/폰 입력 화면 모두 스킵하고
//   초기 입력 화면(Step1 KR / Step2 해외)으로 돌아감.
export const collapseToInitialAndPushRecoverStepAtom = atom(
    null,
    (get, set, nextStep: number) => {
        const stack = get(recoverStepStackAtom);
        const initial = stack[0];
        set(recoverStepStackAtom, [initial, nextStep]);
        history.replaceState({ recoverStep: nextStep }, "");
    }
);

// 결과 분기 타입
export type RecoverProvider = "EMAIL" | "KAKAO" | "NAVER" | "GOOGLE" | "APPLE" | "LINE";

// 데이터
export interface RecoverData {
    // Step1 입력
    name: string;             // 이름 (KR)
    telecom: string;          // 통신사 (KR)
    birth: string;            // 생년월일 yyyyMMdd (KR — 다날 본인인증 필수)
    gender: "MALE" | "FEMALE" | "OTHER" | "";  // 성별 (KR — 다날 본인인증 필수)
    ctn: string;              // 전화번호
    ctnCountryCd: string;     // 국가전화코드 (해외)
    ctnCountry: string;       // 전화번호 ISO2 국가 ("US") — 해외 picker 선택값 보존용.
                              //   ctnCountryCd 만으론 국가를 특정할 수 없다 (+1 = US/CA 등 25개국,
                              //   +7 = RU/KZ). Step3 갔다 돌아올 때 선택을 복원하려면 ISO2 가 필요하다.
    nationality: "KOREAN" | "FOREIGNER";  // KR 다날 내/외국인 (genderDigit 1~4 / 5~8) — 인증 전용

    // Step2 인증 결과
    verifyId: number | null;                  // 해외 OTP 검증 ID
    identityVerificationId: string;           // 다날 본인인증 ID

    // Step3 결과
    foundEmail: string;                       // 마스킹된 이메일/아이디
    foundProvider: RecoverProvider | "";      // EMAIL 외엔 SNS 안내
    foundSignedUpDate: string;                // 가입 일자 (ISO) — 미가입 시 빈 문자열
    recoveryToken: string;                    // EMAIL 일 때만 발급 (5분 TTL, 일회용)
    accountFound: boolean;                    // false → 가입 이력 없음
    findAccountFailed: boolean;               // true → find-account API 오류 (서버/네트워크). '미가입' 과 구분
}

const initialRecoverData: RecoverData = {
    name: "",
    telecom: "",
    birth: "",
    gender: "",
    ctn: "",
    ctnCountryCd: "",
    ctnCountry: "",
    nationality: "KOREAN",
    verifyId: null,
    identityVerificationId: "",
    foundEmail: "",
    foundProvider: "",
    foundSignedUpDate: "",
    recoveryToken: "",
    accountFound: false,
    findAccountFailed: false,
};

export const recoverDataAtom = atom<RecoverData>(initialRecoverData);

export const resetRecoverData = atom(
    null,
    (_get, set) => {
        set(recoverDataAtom, initialRecoverData);
        set(recoverStepStackAtom, [1]);
        set(recoverHistoryDepthAtom, 0);
    }
);
